import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Plus, 
  Search, 
  Download, 
  Eye, 
  Check, 
  X, 
  Clock, 
  MapPin, 
  Building, 
  Calendar,
  FileText
} from 'lucide-react';
import PermissionGate from '../../../components/common/PermissionGate';
import Button from '../../../components/common/Button/Button';

const EmployeeTransfer = ({ onBack, userRole }) => {
  const [activeTab, setActiveTab] = useState('requests');
  const [transfers, setTransfers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);

  // Mock transfer data
  useEffect(() => {
    const mockTransfers = [
      {
        id: 'TR001',
        employeeId: 'EMP001',
        employeeName: 'John Doe',
        currentDepartment: 'IT Department',
        currentLocation: 'Mumbai Office',
        requestedDepartment: 'Sales Department',
        requestedLocation: 'Delhi Office',
        requestDate: '2024-01-15',
        effectiveDate: '2024-02-01',
        reason: 'Career growth opportunity',
        status: 'pending',
        requestedBy: 'John Doe',
        approvedBy: null,
        comments: 'Looking for new challenges in sales domain'
      },
      {
        id: 'TR002',
        employeeId: 'EMP002',
        employeeName: 'Jane Smith',
        currentDepartment: 'HR Department',
        currentLocation: 'Delhi Office',
        requestedDepartment: 'Operations',
        requestedLocation: 'Bangalore Office',
        requestDate: '2024-01-10',
        effectiveDate: '2024-01-25',
        reason: 'Family relocation',
        status: 'approved',
        requestedBy: 'Jane Smith',
        approvedBy: 'HR Manager',
        comments: 'Family moved to Bangalore for personal reasons'
      },
      {
        id: 'TR003',
        employeeId: 'EMP003',
        employeeName: 'Mike Johnson',
        currentDepartment: 'Finance',
        currentLocation: 'Mumbai Office',
        requestedDepartment: 'Finance',
        requestedLocation: 'Chennai Office',
        requestDate: '2024-01-08',
        effectiveDate: '2024-01-20',
        reason: 'Project requirement',
        status: 'rejected',
        requestedBy: 'Finance Manager',
        approvedBy: 'HR Manager',
        comments: 'Current project requires presence in Mumbai'
      }
    ];
    setTransfers(mockTransfers);
  }, []);

  const filteredTransfers = transfers.filter(transfer => {
    const matchesSearch = transfer.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transfer.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || transfer.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'approved': return <Check className="w-4 h-4" />;
      case 'rejected': return <X className="w-4 h-4" />;
      case 'completed': return <Check className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const handleApproveTransfer = (transferId) => {
    setTransfers(prev => prev.map(transfer => 
      transfer.id === transferId 
        ? { ...transfer, status: 'approved', approvedBy: 'Current User' }
        : transfer
    ));
  };

  const handleRejectTransfer = (transferId) => {
    setTransfers(prev => prev.map(transfer => 
      transfer.id === transferId 
        ? { ...transfer, status: 'rejected', approvedBy: 'Current User' }
        : transfer
    ));
  };

  const TransferForm = () => {
    const [formData, setFormData] = useState({
      employeeId: '',
      currentDepartment: '',
      currentLocation: '',
      requestedDepartment: '',
      requestedLocation: '',
      effectiveDate: '',
      reason: '',
      comments: ''
    });
    const [errors, setErrors] = useState({});

    // Handle key press for text inputs (alphanumeric, spaces, commas only)
    const handleTextKeyPress = (e) => {
      if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
      }
    };

    const handleInputChange = (e) => {
      const { name, value } = e.target;
      let processedValue = value;
      
      // Text fields: only allow alphanumeric, spaces, and commas
      if (name === 'employeeId') {
        processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 20);
      } else if (name === 'comments') {
        processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 1000);
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: processedValue || value
      }));
      
      // Clear error when user starts typing
      if (errors[name]) {
        setErrors(prev => ({
          ...prev,
          [name]: undefined
        }));
      }
    };

    const validateForm = () => {
      const newErrors = {};
      
      if (!formData.employeeId.trim()) {
        newErrors.employeeId = 'Employee ID is required.';
      } else if (formData.employeeId.length > 20) {
        newErrors.employeeId = 'Employee ID must be ≤ 20 characters.';
      } else if (!/^[a-zA-Z0-9\s,]+$/.test(formData.employeeId)) {
        newErrors.employeeId = 'Employee ID can only contain alphanumeric characters, spaces, and commas.';
      }
      
      if (formData.comments && formData.comments.length > 1000) {
        newErrors.comments = 'Comments must be ≤ 1000 characters.';
      } else if (formData.comments && !/^[a-zA-Z0-9\s,]+$/.test(formData.comments)) {
        newErrors.comments = 'Comments can only contain alphanumeric characters, spaces, and commas.';
      }
      
      if (!formData.effectiveDate) {
        newErrors.effectiveDate = 'Effective date is required.';
      }
      
      if (!formData.reason) {
        newErrors.reason = 'Reason is required.';
      }
      
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      
      if (!validateForm()) {
        return;
      }
      
      // Check for required fields
      if (!formData.employeeId.trim() || !formData.effectiveDate || !formData.reason) {
        setErrors({
          employeeId: !formData.employeeId.trim() ? 'Employee ID is required.' : undefined,
          effectiveDate: !formData.effectiveDate ? 'Effective date is required.' : undefined,
          reason: !formData.reason ? 'Reason is required.' : undefined
        });
        return;
      }
      
      const newTransfer = {
        id: `TR${String(transfers.length + 1).padStart(3, '0')}`,
        ...formData,
        requestDate: new Date().toISOString().split('T')[0],
        status: 'pending',
        requestedBy: 'Current User',
        approvedBy: null
      };
      setTransfers(prev => [...prev, newTransfer]);
      setShowTransferForm(false);
      setFormData({
        employeeId: '',
        currentDepartment: '',
        currentLocation: '',
        requestedDepartment: '',
        requestedLocation: '',
        effectiveDate: '',
        reason: '',
        comments: ''
      });
      setErrors({});
    };

    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">New Transfer Request</h3>
          <Button
            variant="outline"
            onClick={() => setShowTransferForm(false)}
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employee ID
              </label>
              <input
                type="text"
                name="employeeId"
                value={formData.employeeId}
                onChange={handleInputChange}
                onKeyPress={handleTextKeyPress}
                maxLength={20}
                required
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.employeeId ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              <div className="flex justify-between items-center mt-1">
                {errors.employeeId && (
                  <p className="text-sm text-red-600">{errors.employeeId}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.employeeId.length}/20 characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Department
              </label>
              <select
                value={formData.currentDepartment}
                onChange={(e) => setFormData(prev => ({ ...prev, currentDepartment: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Department</option>
                <option value="IT Department">IT Department</option>
                <option value="HR Department">HR Department</option>
                <option value="Finance">Finance</option>
                <option value="Sales Department">Sales Department</option>
                <option value="Operations">Operations</option>
                <option value="Marketing">Marketing</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Location
              </label>
              <select
                value={formData.currentLocation}
                onChange={(e) => setFormData(prev => ({ ...prev, currentLocation: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Location</option>
                <option value="Mumbai Office">Mumbai Office</option>
                <option value="Delhi Office">Delhi Office</option>
                <option value="Bangalore Office">Bangalore Office</option>
                <option value="Chennai Office">Chennai Office</option>
                <option value="Pune Office">Pune Office</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Requested Department
              </label>
              <select
                value={formData.requestedDepartment}
                onChange={(e) => setFormData(prev => ({ ...prev, requestedDepartment: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Department</option>
                <option value="IT Department">IT Department</option>
                <option value="HR Department">HR Department</option>
                <option value="Finance">Finance</option>
                <option value="Sales Department">Sales Department</option>
                <option value="Operations">Operations</option>
                <option value="Marketing">Marketing</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Requested Location
              </label>
              <select
                value={formData.requestedLocation}
                onChange={(e) => setFormData(prev => ({ ...prev, requestedLocation: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Location</option>
                <option value="Mumbai Office">Mumbai Office</option>
                <option value="Delhi Office">Delhi Office</option>
                <option value="Bangalore Office">Bangalore Office</option>
                <option value="Chennai Office">Chennai Office</option>
                <option value="Pune Office">Pune Office</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Effective Date
              </label>
              <input
                type="date"
                name="effectiveDate"
                value={formData.effectiveDate}
                onChange={handleInputChange}
                required
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.effectiveDate ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.effectiveDate && (
                <p className="mt-1 text-sm text-red-600">{errors.effectiveDate}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Transfer
            </label>
            <select
              name="reason"
              value={formData.reason}
              onChange={handleInputChange}
              required
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.reason ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Select Reason</option>
              <option value="Career growth opportunity">Career growth opportunity</option>
              <option value="Family relocation">Family relocation</option>
              <option value="Project requirement">Project requirement</option>
              <option value="Personal reasons">Personal reasons</option>
              <option value="Skill development">Skill development</option>
              <option value="Business needs">Business needs</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Comments
            </label>
            <textarea
              name="comments"
              value={formData.comments}
              onChange={handleInputChange}
              onKeyPress={handleTextKeyPress}
              maxLength={1000}
              rows={4}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.comments ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Provide additional details about the transfer request..."
            />
            <div className="flex justify-between items-center mt-1">
              {errors.comments && (
                <p className="text-sm text-red-600">{errors.comments}</p>
              )}
              <p className="text-xs text-gray-500 ml-auto">
                {formData.comments.length}/1000 characters
              </p>
            </div>
            {errors.effectiveDate && (
              <p className="mt-1 text-sm text-red-600">{errors.effectiveDate}</p>
            )}
            {errors.reason && (
              <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
            )}
          </div>

          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowTransferForm(false)}
            >
              Cancel
            </Button>
            <PermissionGate permission="employee_management" action="create">
              <Button type="submit">
                Submit Request
              </Button>
            </PermissionGate>
          </div>
        </form>
      </div>
    );
  };

  if (showTransferForm) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center mb-6">
            <Button
              variant="outline"
              onClick={() => setShowTransferForm(false)}
              className="mr-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">Employee Transfer Request</h1>
          </div>
          <TransferForm />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            {onBack && (
              <Button
                variant="outline"
                onClick={onBack}
                className="mr-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            )}
            <h1 className="text-2xl font-bold text-gray-900">Employee Transfers</h1>
          </div>
          <PermissionGate permission="employee_management" action="create">
            <Button onClick={() => setShowTransferForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Transfer Request
            </Button>
          </PermissionGate>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {[
                { id: 'requests', label: 'Transfer Requests', count: transfers.length },
                { id: 'pending', label: 'Pending Approvals', count: transfers.filter(t => t.status === 'pending').length },
                { id: 'history', label: 'Transfer History', count: transfers.filter(t => t.status !== 'pending').length }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                  <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                    {tab.count}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          {/* Filters */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search by employee name or transfer ID..."
                    value={searchTerm}
                    onChange={(e) => {
                      // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                      const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                      setSearchTerm(value);
                    }}
                    onKeyPress={(e) => {
                      // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                      if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    maxLength={100}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="completed">Completed</option>
                </select>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Transfer List */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transfer Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requested
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dates
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTransfers.map((transfer) => (
                  <tr key={transfer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {transfer.employeeName}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {transfer.id} • {transfer.employeeId}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center mb-1">
                          <Building className="w-4 h-4 mr-1 text-gray-400" />
                          {transfer.currentDepartment}
                        </div>
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1 text-gray-400" />
                          {transfer.currentLocation}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center mb-1">
                          <Building className="w-4 h-4 mr-1 text-gray-400" />
                          {transfer.requestedDepartment}
                        </div>
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1 text-gray-400" />
                          {transfer.requestedLocation}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(transfer.status)}`}>
                        {getStatusIcon(transfer.status)}
                        <span className="ml-1 capitalize">{transfer.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center mb-1">
                        <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                        Requested: {transfer.requestDate}
                      </div>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                        Effective: {transfer.effectiveDate}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedTransfer(transfer)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {transfer.status === 'pending' && (userRole === 'SUPER_ADMIN' || userRole === 'HR_MANAGER') && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleApproveTransfer(transfer.id)}
                              className="text-green-600 hover:text-green-700"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRejectTransfer(transfer.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredTransfers.length === 0 && (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No transfer requests found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || filterStatus !== 'all' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Get started by creating a new transfer request.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Transfer Details Modal */}
      {selectedTransfer && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 !mt-0">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Transfer Request Details</h3>
              <Button
                variant="outline"
                onClick={() => setSelectedTransfer(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Employee</label>
                  <p className="text-sm text-gray-900">{selectedTransfer.employeeName} ({selectedTransfer.employeeId})</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Transfer ID</label>
                  <p className="text-sm text-gray-900">{selectedTransfer.id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Current Department</label>
                  <p className="text-sm text-gray-900">{selectedTransfer.currentDepartment}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Requested Department</label>
                  <p className="text-sm text-gray-900">{selectedTransfer.requestedDepartment}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Current Location</label>
                  <p className="text-sm text-gray-900">{selectedTransfer.currentLocation}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Requested Location</label>
                  <p className="text-sm text-gray-900">{selectedTransfer.requestedLocation}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Request Date</label>
                  <p className="text-sm text-gray-900">{selectedTransfer.requestDate}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Effective Date</label>
                  <p className="text-sm text-gray-900">{selectedTransfer.effectiveDate}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Reason</label>
                  <p className="text-sm text-gray-900">{selectedTransfer.reason}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedTransfer.status)}`}>
                    {getStatusIcon(selectedTransfer.status)}
                    <span className="ml-1 capitalize">{selectedTransfer.status}</span>
                  </span>
                </div>
              </div>
              
              {selectedTransfer.comments && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Comments</label>
                  <p className="text-sm text-gray-900">{selectedTransfer.comments}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Requested By</label>
                  <p className="text-sm text-gray-900">{selectedTransfer.requestedBy}</p>
                </div>
                {selectedTransfer.approvedBy && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Approved By</label>
                    <p className="text-sm text-gray-900">{selectedTransfer.approvedBy}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeTransfer;